import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { portalApi, usePortal } from "@/lib/PortalContext";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, KeyRound } from "lucide-react";

export default function PortalLogin() {
  const nav = useNavigate();
  const location = useLocation();
  const { setPortalClient } = usePortal();
  const nextPath = new URLSearchParams(location.search).get("next") || "/portal";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const [linkEmail, setLinkEmail] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter your email and password");
    setBusy(true);
    try {
      await portalApi.post("/portal/login", { email, password });
      const me = await portalApi.get("/portal/me");
      setPortalClient(me.data);
      nav(nextPath.startsWith("/portal") ? nextPath : "/portal");
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    if (!linkEmail) return toast.error("Enter your email");
    setLinkBusy(true);
    try {
      await portalApi.post("/portal/request-magic-link", { email: linkEmail });
      setLinkSent(true);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLinkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))] flex items-center justify-center px-6 py-14" data-testid="portal-login">
      <div className="max-w-md w-full">
        <div className="overline text-center">Subtend Studio · Portal</div>
        <h1 className="font-serif font-light text-center mt-4" style={{ fontSize: "44px", lineHeight: 1.05 }}>
          Sign in to your Client Portal
        </h1>

        {/* Password sign-in */}
        <form onSubmit={doLogin} className="luxe-card p-7 mt-10 space-y-4" data-testid="portal-login-form">
          <div>
            <label className="overline text-[10px] block mb-1">Email</label>
            <div className="relative">
              <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
              <input
                data-testid="input-login-email"
                type="email" autoComplete="email"
                className="luxe-input pl-9"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="overline text-[10px]">Password</label>
              <Link to="/portal/forgot-password" className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-forgot">Forgot password?</Link>
            </div>
            <div className="relative">
              <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
              <input
                data-testid="input-login-password"
                type={showPw ? "text" : "password"} autoComplete="current-password"
                className="luxe-input pl-9 pr-10"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button" tabIndex={-1}
                data-testid="btn-toggle-pw"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
              </button>
            </div>
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="btn-login-submit"
            className="btn-primary w-full justify-center"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-8">
          <div className="flex-1 h-px bg-border"/>
          <div className="overline text-[10px] text-muted-foreground">OR</div>
          <div className="flex-1 h-px bg-border"/>
        </div>

        {/* Magic link */}
        <form onSubmit={sendMagicLink} className="luxe-card p-7 space-y-4" data-testid="portal-magic-form">
          <div className="flex items-start gap-3">
            <KeyRound className="w-5 h-5 mt-1 text-[color:hsl(var(--accent))]" strokeWidth={1.5}/>
            <div>
              <div className="font-serif text-xl">Continue with Magic Link</div>
              <p className="text-xs text-muted-foreground mt-1">We'll email you a secure one-time link — no password needed.</p>
            </div>
          </div>
          {linkSent ? (
            <div className="text-sm text-muted-foreground border-l-2 border-[color:hsl(var(--accent))] pl-4 py-1" data-testid="magic-sent-msg">
              If <strong>{linkEmail}</strong> is registered with your studio, a sign-in link is on its way. It expires in 45 minutes.
            </div>
          ) : (
            <>
              <div>
                <label className="overline text-[10px] block mb-1">Email</label>
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
                  <input
                    data-testid="input-magic-email"
                    type="email" autoComplete="email"
                    className="luxe-input pl-9"
                    value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>
              <button
                type="submit" disabled={linkBusy}
                data-testid="btn-magic-submit"
                className="btn-outline w-full justify-center"
              >
                {linkBusy ? "Sending…" : "Send Magic Link"}
              </button>
            </>
          )}
          <p className="text-[11px] text-muted-foreground">
            No account yet? Reach out to the studio on <a href="mailto:hello@luxemediawalls.com.au" className="underline">hello@luxemediawalls.com.au</a> and we'll invite you.
          </p>
        </form>

        <div className="text-center mt-8">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground" data-testid="link-home">← Return to homepage</Link>
        </div>
      </div>
    </div>
  );
}
